/**
 * Website Editor Page — Main editor view for AI-generated websites.
 *
 * Layout: Toolbar (top) + SectionNavigator (left) + PreviewFrame (center) + ChatRefinement (right)
 *
 * Route: /projects/:projectId/editor/:jobId
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, RotateCcw, Minimize2 } from 'lucide-react';
import api from '../../lib/api';
import { qk } from '@/lib/queryKeys';

import { Toolbar } from '../../components/editor/Toolbar';
import { PreviewFrame } from '../../components/editor/PreviewFrame';
import { SectionNavigator } from '../../components/editor/SectionNavigator';
import { ChatRefinement } from '../../components/editor/ChatRefinement';
import { MediaPicker } from '../../components/editor/MediaPicker';
import { VersionHistory } from '../../components/editor/VersionHistory';
import { SectionStudio } from '../../components/editor/SectionStudio';
import { BuildStatusBanner } from '../../components/editor/BuildStatusBanner';

/** Statuses the generator pipeline never leaves on its own. Polling stops here,
 *  and this is the moment the project's Versions list has new content. */
const TERMINAL_JOB_STATUSES = ['complete', 'error', 'needs_review'] as const;

function isTerminalJobStatus(status: unknown): boolean {
  return TERMINAL_JOB_STATUSES.includes(status as (typeof TERMINAL_JOB_STATUSES)[number]);
}

export default function WebsiteEditor() {
  const { projectId, jobId } = useParams<{ projectId: string; jobId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [showChat, setShowChat] = useState(false);
  const [showMedia, setShowMedia] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [fullPreview, setFullPreview] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);

  // Fetch job details
  const { data: jobData, isLoading: jobLoading } = useQuery({
    queryKey: qk.generationJob(projectId, jobId),
    queryFn: async () => {
      const res = await api.get(`/generator/${projectId}/jobs/${jobId}`);
      return res.data.data;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Stop polling for terminal states
      if (isTerminalJobStatus(status)) return false;
      // Poll faster during active generation (3s) for responsive progress bar
      return 3000;
    },
  });

  // A run that finishes adds a row to the project's Versions tab, which reads a
  // SEPARATE cache entry (qk.generationJobs) that nothing in this page writes —
  // so without this the list stayed stale until a manual reload. Keyed on the
  // terminal status so it fires once per transition, not on every 3s poll, and
  // resets when a resume puts the job back in flight.
  const settledStatusRef = useRef<string | null>(null);
  useEffect(() => {
    const status = jobData?.status;
    if (!isTerminalJobStatus(status)) {
      settledStatusRef.current = null;
      return;
    }
    if (settledStatusRef.current === status) return;
    settledStatusRef.current = status;
    queryClient.invalidateQueries({ queryKey: qk.generationJobs(projectId) });
  }, [jobData?.status, projectId, queryClient]);

  // Fetch content
  const { refetch: refetchContent } = useQuery({
    queryKey: ['editor-content', projectId, jobId],
    queryFn: async () => {
      const res = await api.get(`/editor/${projectId}/${jobId}/content`);
      return res.data.data;
    },
    enabled: jobData?.status === 'complete',
  });

  // Fetch sections
  const { data: sectionsData, refetch: refetchSections } = useQuery({
    queryKey: ['editor-sections', projectId, jobId],
    queryFn: async () => {
      const res = await api.get(`/editor/${projectId}/${jobId}/sections`);
      return res.data.data;
    },
    enabled: jobData?.status === 'complete',
  });

  // Save HTML directly (from inline editing in preview iframe)
  const saveHtml = useMutation({
    mutationFn: async ({ html, page }: { html: string; page: string }) => {
      await api.put(`/editor/${projectId}/${jobId}/html`, { html, page });
    },
    onSuccess: () => toast.success('Changes saved', { duration: 1500 }),
    onError: () => toast.error('Failed to save changes'),
  });

  // Handle inline edits from the preview iframe (via postMessage)
  const handleHtmlEdit = useCallback(
    (html: string) => {
      // Determine which page is being edited from the preview URL
      const page = '/'; // TODO: detect from iframe navigation
      saveHtml.mutate({ html, page });
    },
    [projectId, jobId]
  );

  // Handle image click in preview (open media picker)
  const handleImageClick = useCallback((_src: string) => {
    // Open media picker — when image is selected, send it back to iframe
    setShowMedia(true);
    setShowChat(false);
    setShowVersions(false);
    toast.info('Select a replacement image', { duration: 2000 });
  }, []);

  // Section operations
  const addSection = useMutation({
    mutationFn: async ({ type, position }: { type: string; position: number }) => {
      const res = await api.post(`/editor/${projectId}/${jobId}/sections`, { type, position });
      return res.data.data;
    },
    onSuccess: () => {
      refetchSections();
      refetchContent();
      toast.success('Section added');
    },
    onError: () => toast.error('Failed to add section'),
  });

  const removeSection = useMutation({
    mutationFn: async (sectionId: string) => {
      await api.delete(`/editor/${projectId}/${jobId}/sections/${sectionId}`);
    },
    onSuccess: () => {
      refetchSections();
      refetchContent();
      toast.success('Section removed');
    },
    onError: () => toast.error('Failed to remove section'),
  });

  const reorderSections = useMutation({
    mutationFn: async (order: string[]) => {
      await api.put(`/editor/${projectId}/${jobId}/sections/reorder`, { order });
    },
    onSuccess: () => {
      refetchSections();
      toast.success('Sections reordered');
    },
  });

  // Resume a failed or stalled job
  const resumeJob = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/generator/${projectId}/jobs/${jobId}/resume`);
      return res.data.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Pipeline resuming...');
      // Optimistically switch UI to "in progress" immediately so the user
      // doesn't stay on the error/stalled screen while waiting for the next poll.
      queryClient.setQueryData(qk.generationJob(projectId, jobId), (old: any) => ({
        ...old,
        status: 'pending',
        progress: 0,
        currentStep: 'Resuming pipeline...',
        error: undefined,
      }));
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Failed to resume job');
    },
  });

  // Detect stalled jobs — active status but no update for >3 minutes
  const isStalled =
    jobData &&
    !['complete', 'error'].includes(jobData.status) &&
    jobData.updatedAt &&
    Date.now() - new Date(jobData.updatedAt).getTime() > 3 * 60 * 1000;

  const cloneSection = useMutation({
    mutationFn: async (sectionId: string) => {
      const res = await api.post(`/editor/${projectId}/${jobId}/sections/${sectionId}/clone`);
      return res.data.data;
    },
    onSuccess: () => {
      refetchSections();
      refetchContent();
      toast.success('Section cloned');
    },
  });

  // Preview URL
  const previewUrl =
    jobData?.status === 'complete'
      ? `${import.meta.env.VITE_API_URL || '/api/v1'}/generator/${projectId}/${jobId}/preview/index.html`
      : null;

  // Loading state
  if (jobLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
          <p className="mt-4 text-gray-600">Loading editor...</p>
        </div>
      </div>
    );
  }

  // Generation in progress
  if (jobData && jobData.status !== 'complete' && jobData.status !== 'error') {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="max-w-md text-center">
          {isStalled ? (
            <>
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-xl font-bold text-amber-600">
                ⏸
              </div>
              <h2 className="mt-4 text-xl font-semibold text-amber-600">Generation Stalled</h2>
              <p className="mt-2 text-sm text-gray-600">
                Pipeline stopped at "{jobData.currentStep}" ({jobData.progress}%). This typically
                happens when the server times out during an AI call. Your completed steps are
                preserved.
              </p>
              <button
                onClick={() => resumeJob.mutate()}
                disabled={resumeJob.isPending}
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {resumeJob.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4" />
                )}
                Resume from {jobData.progress}%
              </button>
            </>
          ) : (
            <>
              <Loader2 className="mx-auto h-10 w-10 animate-spin text-blue-600" />
              <h2 className="mt-4 text-xl font-semibold">
                {jobData.currentStep || 'Generating...'}
              </h2>
              <div className="mx-auto mt-4 h-2.5 w-72 overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-700 ease-out"
                  style={{ width: `${jobData.progress || 0}%` }}
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">{jobData.progress || 0}% complete</p>
              {jobData.status === 'building' && (
                <p className="mt-1 text-xs text-gray-400">Building and deploying your website...</p>
              )}
              {jobData.tokenUsage?.totalCostUsd > 0 && (
                <p className="mt-3 text-xs text-gray-400">
                  AI cost: ${jobData.tokenUsage.totalCostUsd.toFixed(4)}
                </p>
              )}
            </>
          )}
          <button
            onClick={() => navigate(`/projects/${projectId}`)}
            className="mt-6 text-sm text-blue-600 hover:underline"
          >
            ← Back to project
          </button>
        </div>
      </div>
    );
  }

  // Error state
  if (jobData?.status === 'error' || jobData?.status === 'needs_review') {
    const completedSteps = Object.keys(jobData.agentOutputs || {});
    const isNeedsReview = jobData.status === 'needs_review';
    return (
      <div className="flex h-screen flex-col bg-gray-50">
        <BuildStatusBanner projectId={projectId!} />
        <div className="flex flex-1 items-center justify-center">
          <div className="max-w-md text-center">
            <div
              className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full text-xl font-bold ${isNeedsReview ? 'bg-amber-100 text-amber-600' : 'bg-red-100 text-red-600'}`}
            >
              {isNeedsReview ? '?' : '!'}
            </div>
            <h2
              className={`mt-4 text-xl font-semibold ${isNeedsReview ? 'text-amber-600' : 'text-red-600'}`}
            >
              {isNeedsReview ? 'Needs Review' : 'Generation Failed'}
            </h2>
            <p className="mt-2 text-gray-600">
              {jobData.error?.message ||
                (isNeedsReview
                  ? 'This job needs manual review before retrying.'
                  : 'An unknown error occurred.')}
            </p>
            {completedSteps.length > 0 && (
              <p className="mt-1 text-xs text-gray-400">
                Completed: {completedSteps.join(', ')} — these won't be re-run
              </p>
            )}
            {jobData.tokenUsage?.totalCostUsd > 0 && (
              <p className="mt-1 text-xs text-gray-400">
                Cost so far: ${jobData.tokenUsage.totalCostUsd.toFixed(4)}
              </p>
            )}
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                onClick={() => resumeJob.mutate()}
                disabled={resumeJob.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {resumeJob.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4" />
                )}
                Resume
              </button>
              <button
                onClick={() => navigate(`/projects/${projectId}`)}
                className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm hover:bg-gray-200"
              >
                <ArrowLeft className="h-4 w-4" /> Back to project
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Full-screen preview overlay
  if (fullPreview && previewUrl) {
    return (
      <div className="fixed inset-0 z-50 bg-white">
        <iframe
          src={previewUrl}
          className="h-full w-full border-0"
          title="Website Preview (Full Screen)"
          sandbox="allow-scripts allow-same-origin"
        />
        <button
          onClick={() => setFullPreview(false)}
          className="fixed right-4 top-4 z-[60] flex items-center gap-1.5 rounded-lg bg-gray-900/80 px-3 py-2 text-xs font-medium text-white shadow-lg backdrop-blur transition-colors hover:bg-gray-900"
          title="Exit full-screen preview"
        >
          <Minimize2 className="h-4 w-4" />
          Exit Preview
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gray-100">
      {/* Toolbar */}
      <Toolbar
        device={device}
        onDeviceChange={setDevice}
        onToggleChat={() => {
          setShowChat(!showChat);
          if (!showChat) {
            setShowMedia(false);
            setShowVersions(false);
          }
        }}
        onToggleMedia={() => {
          setShowMedia(!showMedia);
          if (!showMedia) {
            setShowChat(false);
            setShowVersions(false);
          }
        }}
        onToggleVersions={() => {
          setShowVersions(!showVersions);
          if (!showVersions) {
            setShowChat(false);
            setShowMedia(false);
          }
        }}
        isChatOpen={showChat}
        isVersionsOpen={showVersions}
        isSaving={saveHtml.isPending}
        projectId={projectId!}
        jobId={jobId!}
        onBack={() => navigate(`/projects/${projectId}`)}
        onToggleFullPreview={() => setFullPreview(true)}
      />

      {/* Build-failure banner — only renders when the most recent GitHub Actions
          build for this project failed. Surfaces Repair package.json + Rebuild
          inline so users don't have to dig through the Versions panel. */}
      <BuildStatusBanner projectId={projectId!} />

      {/* Main editor area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Section Navigator (left) */}
        <SectionNavigator
          sections={sectionsData || []}
          selectedSection={selectedSection}
          onSelectSection={setSelectedSection}
          onAddSection={(type, position) => addSection.mutate({ type, position })}
          onRemoveSection={(id) => removeSection.mutate(id)}
          onCloneSection={(id) => cloneSection.mutate(id)}
          onReorder={(order) => reorderSections.mutate(order)}
        />

        {/* Preview Frame (center) */}
        <div className="flex flex-1 items-center justify-center overflow-auto p-4">
          <PreviewFrame
            key={previewKey}
            url={previewUrl}
            device={device}
            editMode={true}
            selectedSection={selectedSection}
            onSectionClick={setSelectedSection}
            onHtmlEdit={handleHtmlEdit}
            onImageClick={handleImageClick}
          />
        </div>

        {/* Chat Refinement (right) */}
        {showChat && (
          <ChatRefinement
            projectId={projectId!}
            jobId={jobId!}
            onContentUpdated={() => {
              refetchContent();
              refetchSections();
              setPreviewKey((k) => k + 1);
            }}
            onClose={() => setShowChat(false)}
          />
        )}

        {/* Media Picker (right) */}
        {showMedia && (
          <MediaPicker
            projectId={projectId!}
            jobId={jobId!}
            onSelect={(url) => {
              toast.success(`Image selected: ${url}`);
              setShowMedia(false);
            }}
            onClose={() => setShowMedia(false)}
          />
        )}

        {/* Version History & Deploy (right) */}
        {showVersions && (
          <VersionHistory
            projectId={projectId!}
            jobId={jobId!}
            onContentUpdated={() => {
              refetchContent();
              refetchSections();
              queryClient.invalidateQueries({ queryKey: qk.generationJob(projectId, jobId) });
            }}
            onClose={() => setShowVersions(false)}
          />
        )}

        {/* Section Studio (right) — auto-opens when a section is selected and no other panel is open.
            `key={selectedSection}` forces a remount when the user switches sections, so local state
            (active tab, in-flight variant batch) doesn't bleed across sections. */}
        {selectedSection && !showChat && !showMedia && !showVersions && (
          <SectionStudio
            key={selectedSection}
            projectId={projectId!}
            jobId={jobId!}
            sectionId={selectedSection}
            onClose={() => setSelectedSection(null)}
            onContentUpdated={() => {
              refetchContent();
              refetchSections();
              setPreviewKey((k) => k + 1);
            }}
          />
        )}
      </div>
    </div>
  );
}
